import React, { useState, useEffect } from 'react';
import './Contact.css';
import Toast from '../components/Toast';

export default function Contact() {

  useEffect(() => {
    document.title = 'Contact — SocialManiacs';
  }, []);

  /* ===============================
     FORM STATE
  =============================== */
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    company: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Simple client-side CAPTCHA (no backend changes)
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);

  useEffect(() => {
    // generate initial captcha
    generateCaptcha();
  }, []);

  function generateCaptcha() {
    const a = Math.floor(Math.random() * 9) + 1;
    const b = Math.floor(Math.random() * 9) + 1;
    const op = Math.random() < 0.5 ? '+' : '-';
    const answer = op === '+' ? a + b : a - b;
    setCaptchaQuestion(`${a} ${op} ${b} = ?`);
    setCaptchaAnswer(answer);
    setCaptchaInput('');
    setCaptchaValid(false);
  }

  useEffect(() => {
    const val = parseInt(captchaInput, 10);
    setCaptchaValid(!Number.isNaN(val) && val === captchaAnswer);
  }, [captchaInput, captchaAnswer]);




  /* ===============================
     SUBMIT HANDLER
  =============================== */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!captchaValid) {
      setToast({
        title: 'Captcha',
        message: 'Please solve the captcha correctly.',
        type: 'error'
      });
      return;
    }
    setLoading(true);

    try {

      const res = await fetch('https://api.socialmaniacs.in/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form
        })
      });

      const data = await res.json();

      if (data && data.ok) {
        setToast({
          title: 'Message sent',
          message: "We'll get back to you within 24 hours.",
          type: 'success'
        });

        setForm({
          name: '',
          email: '',
          company: '',
          message: ''
        });
        setCaptchaInput('');
        generateCaptcha();
      } else {
        setToast({
          title: 'Error',
          message:
            (data.error || 'Failed to send message.') +
            ' Please contact us directly at office.socialmaniacs@gmail.com',
          type: 'error'
        });
      }

    } catch (err) {
      setToast({
        title: 'Error',
        message:
          'Network error. Please contact us directly at office.socialmaniacs@gmail.com',
        type: 'error'
      });
    }

    setLoading(false);
  }

  return (
    <div>

      {/* PAGE HEADER */}
      <div className="page-header">
        <div className="page-min-bg" aria-hidden="true">
          <svg className="bg-lines" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M -50 200 Q 100 100, 300 50" className="bg-arc" />
            <path d="M 700 -50 Q 850 100, 1000 300" className="bg-arc" />
            <path d="M 400 300 Q 600 250, 800 300" className="bg-arc center" />
          </svg>
          <div className="blob blob-1" />
          <div className="blob blob-2" />
          <div className="blob blob-3" />
          <div className="ring ring-1" />
          <div className="dots">
            <span className="dot dot-1" />
            <span className="dot dot-2" />
            <span className="dot dot-3" />
            <span className="dot dot-4" />
            <span className="dot dot-5" />
          </div>
        </div>

        <div className="container text-center">
          <h1 className="fw-bold">Contact</h1>
          <p className="text-muted lead">Let's start your project together</p>
          <div className="header-decor mt-4" aria-hidden="true"></div>
        </div>
      </div>

      {/* CONTACT SECTION */}
      <section id="contact" className="py-5">
        <div className="container">
          <div className="row g-4">

            <div className="col-lg-6 scroll-reveal">
              <h2 className="fw-bold">
                Let's Create Something <span className="gradient-text">Amazing</span>
              </h2>
              <p className="text-muted">
                Ready to transform your digital presence? Get in touch with our team
                and let's discuss your next big project.
              </p>

              <div className="mt-4">
                <p className="mb-1 small text-muted">Email</p>
                <p className="fw-semibold">office.socialmaniacs@gmail.com</p>

                <p className="mb-1 small text-muted">Phone</p>
                <p className="fw-semibold">+91 84016 23480</p>
              </div>
            </div>

            <div className="col-lg-6 scroll-reveal">
              <div className="card-custom">
                <form onSubmit={handleSubmit}>

                  <div className="mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      required
                      className="form-control"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      required
                      className="form-control"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Mobile Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={form.mobile}
                      onChange={e => setForm({ ...form, mobile: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Company</label>
                    <input
                      className="form-control"
                      value={form.company}
                      onChange={e => setForm({ ...form, company: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Project Details</label>
                    <textarea
                      required
                      rows={5}
                      className="form-control"
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                    ></textarea>
                  </div>

                  <div className="mb-3 captcha-row">
                    <label className="form-label">Captcha</label>
                    <div className="d-flex gap-2 align-items-center">
                      <div className="captcha-question">{captchaQuestion}</div>
                      <input
                        required
                        className="form-control captcha-input"
                        value={captchaInput}
                        onChange={e => setCaptchaInput(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={generateCaptcha}
                        title="Refresh captcha"
                      >
                        ⟳
                      </button>
                    </div>
                    <small className={`ms-1 ${captchaInput ? (captchaValid ? 'text-success' : 'text-danger') : 'text-muted'}`}>
                      {captchaInput ? (captchaValid ? 'Correct' : 'Incorrect') : 'Solve the captcha to enable submission'}
                    </small>
                  </div>

                  <button
                    className="btn btn-primary-custom w-100"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>

                </form>
              </div>
            </div>

          </div>
        </div>

        {toast && (
          <Toast
            title={toast.title}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

      </section>
    </div>
  );
}
